import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicJobStoriesExperience } from '@/components/public/PublicJobStoriesExperience';
import { TokenExpiredState } from '@/components/public/confirm/TokenExpiredState';
import { Button } from '@/components/atoms/button';
import { linkIsRevoked, usePublicJobStories } from '@/hooks/public/usePublicJobStories';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

export const Route = createFileRoute('/(public)/job-stories/$token')({
  component: RouteComponent,
});

function RouteComponent() {
  const { token } = Route.useParams();
  return <PublicJobStoriesPage token={token} />;
}

/**
 * Exported for its own test: the page is mostly a decision about which of four
 * states a visitor sees, and driving that through a router to assert it would
 * test the router.
 */
export function PublicJobStoriesPage({ token }: { token: string }) {
  const {
    data,
    isLoading,
    isError,
    isFetchNextPageError,
    error,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePublicJobStories(token);

  if (isLoading) {
    return (
      <PublicCenteredPage className="bg-[#f7f7f4] text-sm text-slate-500">
        Loading job stories…
      </PublicCenteredPage>
    );
  }

  /**
   * A whole-page failure state is only honest while there is nothing to show.
   * The query keeps every page it has already loaded when a later one fails, so
   * `isError` on its own is not "the share is broken" — it is just as likely to
   * be "the reader clicked Load more and the second page timed out", and
   * answering that by blanking two hundred and fifty stories they were already
   * reading loses far more than the failure did. Later-page failures are handed
   * to the experience instead, which reports them next to the button that
   * caused them.
   */
  if (!data) {
    if (isError && linkIsRevoked(error)) {
      return <TokenExpiredState message="This job stories link is no longer active." />;
    }
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <AlertTriangle className="mb-4 h-16 w-16 text-slate-300" />
          <h1 className="text-xl font-semibold text-gray-900">These job stories didn’t load.</h1>
          <p className="mt-2 max-w-sm text-sm text-gray-500">
            The link is fine — something went wrong on our end. Try again in a moment.
          </p>
          <Button className="mt-6" onClick={() => void refetch()} disabled={isFetching}>
            {isFetching ? 'Retrying…' : 'Try again'}
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout maxWidth="xl">
      <PublicJobStoriesExperience
        data={data}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={() => void fetchNextPage()}
        loadMoreError={loadMoreError(isFetchNextPageError, error)}
      />
    </PublicLayout>
  );
}

/**
 * The same split the full-page states make, applied to a page the reader asked
 * for after the share was already open: a retired link is permanent and says so
 * without offering a button, anything else is worth one more click.
 *
 * Gated on `isFetchNextPageError` rather than on `isError` so a failed
 * background refetch — which changes nothing the reader can see, because the
 * loaded pages are still on screen — does not put an error under the button.
 */
function loadMoreError(
  isFetchNextPageError: boolean,
  error: unknown,
): { message: string; canRetry: boolean } | null {
  if (!isFetchNextPageError) return null;
  return linkIsRevoked(error)
    ? {
        message: 'This link is no longer active, so the rest of these stories can’t be loaded.',
        canRetry: false,
      }
    : {
        message: 'Something went wrong loading more stories. The ones below are still here.',
        canRetry: true,
      };
}
